import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Terminal, ArrowRight, Loader2, Check, Eye, EyeOff } from "lucide-react";

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    setLoading(true);
    try {
      const r = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Reset failed");
      setSuccess(true);
      setTimeout(() => {
        if (data.autoLogin) {
          window.location.href = "/";
        } else {
          setLocation("/join");
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white font-mono flex flex-col items-center justify-center px-6">
        <h1 className="text-2xl font-black uppercase tracking-tighter text-red-400 mb-4">Invalid Reset Link</h1>
        <p className="text-sm text-white/40 font-sans mb-8">This reset link is missing or malformed.</p>
        <Link href="/forgot-password">
          <span className="text-[11px] text-cyan-400 uppercase tracking-widest cursor-pointer hover:text-cyan-300 transition-colors">Request a new reset link</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-mono flex flex-col">
      <style>{`
        .neon-glow-cyan { text-shadow: 0 0 20px rgba(0, 255, 255, 0.8); }
        .cursor-blink { animation: blink 1s step-end infinite; }
        @keyframes blink { from, to { opacity: 1; } 50% { opacity: 0; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
      `}</style>

      <header className="border-b border-white/5 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link href="/">
            <span className="text-xl font-black tracking-tighter text-cyan-400 neon-glow-cyan cursor-pointer">DRPR</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-white/25 mb-8">
            <Terminal size={12} className="text-cyan-400" />
            <span>Set New Password</span>
            <span className="w-2 h-3 bg-cyan-400/50 cursor-blink ml-1"></span>
          </div>

          <h1 className="text-3xl font-black uppercase tracking-tighter mb-3">
            RESET <span className="text-cyan-400 neon-glow-cyan">PASSWORD</span>
          </h1>
          <p className="text-sm text-white/40 font-sans mb-10">
            Enter your new password below.
          </p>

          {success ? (
            <div className="fade-up flex items-start gap-3 bg-cyan-400/5 border border-cyan-400/20 p-5">
              <Check size={14} className="text-cyan-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-1">Password reset!</p>
                <p className="text-[11px] text-white/40 font-sans">Redirecting you in a moment...</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password (min. 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full bg-black border border-white/10 px-4 py-3 pr-10 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-cyan-400/40 transition-colors"
                  data-testid="input-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="bg-black border border-white/10 px-4 py-3 text-xs font-mono placeholder:text-white/20 focus:outline-none focus:border-cyan-400/40 transition-colors"
                data-testid="input-confirm-password"
              />
              {error && (
                <p className="text-[11px] text-red-400 uppercase tracking-widest" data-testid="text-reset-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-cyan-400/10 border border-cyan-400/40 text-cyan-400 text-xs font-bold uppercase tracking-widest hover:bg-cyan-400 hover:text-black transition-all disabled:opacity-50"
                data-testid="button-reset-submit"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : (
                  <>Set New Password <ArrowRight size={12} /></>
                )}
              </button>
              <Link href="/join">
                <span className="text-[10px] text-white/25 hover:text-white/50 transition-colors uppercase tracking-widest cursor-pointer text-center block mt-1">
                  Back to sign in
                </span>
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
